import { Injectable } from '@angular/core';

import { environment } from '../../../../environments/environment';

@Injectable({
    providedIn: 'root',
})
export class ExternalUrlResolverService
{
    private _rootDomain = (environment.HBARIOT_SUBDOMAIN_ROOT_DOMAIN ?? 'hbariot.com').toLowerCase();
    private _baseOrigin = environment.HBARIOT_SUBDOMAIN_REDIRECT_BASE_ORIGIN;
    private _overrides = this.NormalizeOverrides(environment.HBARIOT_SUBDOMAIN_REDIRECT_OVERRIDES ?? {});

    Resolve = (rawUrl: string | undefined | null) =>
    {
        if (!rawUrl)
            return rawUrl ?? '';

        const parsedUrl = this.TryParseUrl(rawUrl);

        if (!parsedUrl)
            return rawUrl;

        const host = parsedUrl.host.toLowerCase();

        const overrideTarget = this._overrides[host];

        if (overrideTarget)
            return this.BuildRedirectUrl(overrideTarget, [], parsedUrl);

        if (!this._baseOrigin || !host.endsWith(this._rootDomain))
            return parsedUrl.toString();

        const subdomainSegments = this.ExtractSubdomainSegments(host);

        return this.BuildRedirectUrl(this._baseOrigin, subdomainSegments, parsedUrl);
    };

    private NormalizeOverrides(overrides: { [key: string]: string })
    {
        const normalized: { [key: string]: string } = {};

        Object.keys(overrides).forEach(
            (key) =>
            {
                normalized[key.toLowerCase()] = overrides[key];
            });

        return normalized;
    }

    private TryParseUrl = (rawUrl: string) =>
    {
        try
        {
            return new URL(rawUrl);
        }
        catch (error)
        {
            try
            {
                return new URL(rawUrl, this.GetFallbackOrigin());
            }
            catch (_innerError)
            {
                console.warn('Unable to parse external URL.', rawUrl, error);
                return undefined;
            }
        }
    };

    private GetFallbackOrigin = () =>
    {
        if (typeof window !== 'undefined' && window?.location)
            return window.location.origin;

        return 'http://localhost';
    };

    private ExtractSubdomainSegments = (host: string) =>
    {
        if (host === this._rootDomain)
            return [];

        const suffix = '.' + this._rootDomain;

        if (!host.endsWith(suffix))
            return [];

        const subdomainPart = host.slice(0, host.length - suffix.length);

        return subdomainPart.split('.').filter(Boolean);
    };

    private BuildRedirectUrl = (baseOrigin: string, additionalSegments: string[], originalUrl: URL) =>
    {
        const targetBase = this.TryParseUrl(baseOrigin);

        if (!targetBase)
            return baseOrigin;

        const baseSegments = targetBase.pathname.split('/').filter(Boolean);
        const originalSegments = originalUrl.pathname.split('/').filter(Boolean);

        const finalSegments = [
            ...baseSegments,
            ...additionalSegments,
            ...originalSegments,
        ].filter(Boolean);

        targetBase.pathname = finalSegments.length > 0 ? '/' + finalSegments.join('/') : '/';
        targetBase.search = originalUrl.search;
        targetBase.hash = originalUrl.hash;

        return targetBase.toString();
    };
}
